import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { HiOutlineUsers } from "react-icons/hi2";
import { IoMdSearch } from "react-icons/io";
import { LuPieChart } from "react-icons/lu";
import { MdBlockFlipped } from "react-icons/md";
import { RiAlertLine } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import styles from "./css/Report.module.scss";
import ProductTable from "./datatable/ProductTable";

const Report = () => {
  const { token } = useAuthToken();
  const navigate = useNavigate();
  const location = useLocation();

  // define state
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || "",
  );
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredReports, setFilteredReports] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [dashboardCounts, setDashboardCounts] = useState({
    total_users_report: 0,
    pending_users: 0,
    warn_users: 0,
    blocked_users: 0,
  });

  const fetchReport = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (searchTerm) {
        queryParams.set("search", searchTerm);
      }
      queryParams.set("page", currentPage);
      queryParams.set("pageSize", entriesPerPage);
      queryParams.set("sort_by", sortField);
      queryParams.set("order_by", sortOrder);
      queryParams.set("limit", limit);

      const response = await fetch(
        `${LIVE_URL}/admin/list-user-report?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok && data.data && Array.isArray(data.data.user)) {
        setReports(data.data.user);
        setFilteredReports(data.data.user);
        setTotalPages(
          Math.ceil(data.data.pagination.totalCount / entriesPerPage),
        );
        setDashboardCounts({
          total_users_report: data.data.total_users_report || 0,
          pending_users: data.data.pending_users || 0,
          warn_users: data.data.warn_users || 0,
          blocked_users: data.data.blocked_users || 0,
        });
      } else {
        setReports([]);
        setFilteredReports([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      setReports([]);
      setFilteredReports([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchReport();
    }
  }, [token, currentPage, searchTerm, entriesPerPage, sortField, sortOrder]);

  useEffect(() => {
    if (reports) {
      const filtered = reports.filter((report) =>
        report.report_user?.firstname
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
      setFilteredReports(filtered);
    }
  }, [searchTerm, reports]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setEntriesPerPage(newLimit);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredReports.length / newLimit) || 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousTwo = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleNextTwo = () => {
    if (currentPage !== totalPages) {
      setCurrentPage(totalPages);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    if (currentPage > 1) {
      pageNumbers.push(currentPage - 1);
    }
    pageNumbers.push(currentPage);
    if (currentPage < totalPages) {
      pageNumbers.push(currentPage + 1);
    }
    return pageNumbers;
  };

  const updateUrl = (search) => {
    const params = new URLSearchParams(location.search);
    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }
    navigate({ search: params.toString() });
  };

  useEffect(() => {
    updateUrl(searchTerm);
  }, [searchTerm]);

  return (
    <>
      <div className={`${styles.report}`}>
        <header>
          <h2>Report List Management</h2>
        </header>

        {/* report tiles */}
        <div className={`${styles.report_tiles}`}>
          <div className={`${styles.report_card}`}>
            <p
              className={`${styles.report_card_title} ${styles.total_reports}`}
            >
              <HiOutlineUsers />
              Total Reports
            </p>
            <p className={`${styles.report_count}`}>
              {" "}
              {dashboardCounts.total_users_report}
            </p>
          </div>
          <div className={`${styles.report_card}`}>
            <p
              className={`${styles.report_card_title} ${styles.pending_reports}`}
            >
              <LuPieChart />
              Pending Reports
            </p>
            <p className={`${styles.report_count}`}>
              {dashboardCounts.pending_users}
            </p>
          </div>
          <div className={`${styles.report_card}`}>
            <p className={`${styles.report_card_title} ${styles.in_warning}`}>
              <RiAlertLine />
              In Warning
            </p>
            <p className={`${styles.report_count}`}>
              {dashboardCounts.warn_users}
            </p>
          </div>
          <div className={`${styles.report_card}`}>
            <p
              className={`${styles.report_card_title} ${styles.blocked_users}`}
            >
              <MdBlockFlipped />
              Blocked Users
            </p>
            <p className={`${styles.report_count}`}>
              {dashboardCounts.blocked_users}
            </p>
          </div>
        </div>

        {/* entry section */}
        <section className={`${styles.section}`}>
          <div className={`${styles.user_entry}`}>
            <div className={`${styles.entries}`}>
              <label htmlFor="limit">Show</label>
              <select value={limit} onChange={handleLimitChange}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <label htmlFor="entries">entries</label>
            </div>
            <div className={`${styles.search}`}>
              {/* <label htmlFor="search">Search: </label> */}
              <IoMdSearch />
              <input
                type="search"
                id="search"
                value={searchTerm}
                placeholder="Search by report..."
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* user information section */}
          <div className={`${styles.user_info}`}>
            <ProductTable
              apiEndpoint={`${LIVE_URL}/admin/list-user-report`}
              reports={filteredReports}
              setReports={setReports}
              rows={entriesPerPage}
              userName={searchTerm}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              sortField={sortField}
              sortOrder={sortOrder}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              loading={loading}
              setLoading={setLoading}
              refreshReports={fetchReport}
            />
          </div>

          {/* pagination section */}
          <div className={`${styles.pagination}`}>
            <button onClick={handlePreviousTwo} disabled={currentPage === 1}>
              <FiChevronsLeft />
            </button>
            <button onClick={handlePrevious} disabled={currentPage === 1}>
              <FiChevronLeft />
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={currentPage === page ? styles.active : ""}
                disabled={page > totalPages}
              >
                {page}
              </button>
            ))}
            <button onClick={handleNext} disabled={currentPage === totalPages}>
              <FiChevronRight />
            </button>
            <button
              onClick={handleNextTwo}
              disabled={
                currentPage === totalPages || currentPage === totalPages - 1
              }
            >
              <FiChevronsRight />
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Report;
